import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { UPGRADE_LIMIT_MODAL } from 'constants/feature_limits'
import {
  CHINESE_LANGUAGE_CHECKOUT_LOCALES,
  CN_PRICING_PAGE_URL
} from 'constants/account'
import { SETTINGS_URLS } from 'constants/routes'
import { Button, Icon, Modal } from '@junglescout/edna'

import { setGlobalModal } from 'actions/index'
import { sendCtaClickEvent } from 'helpers/segment'
import { getPricingPageUrl } from 'helpers/account'

import HoverGraphic from './hover.svg'

const CloseButton = styled(Icon)`
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;

  color: ${props => props.theme.colors.grey600};
`
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`
const UsageCounter = styled.div`
  position: relative;

  width: 45px;
  text-align: center;

  color: ${props => props.theme.colors.grey600};

  font-weight: 600;
  font-size: 12px;
  line-height: 24px;

  margin: auto;
`
const UsageCounterGraphic = styled.img`
  position: absolute;
  z-index: -1;
  margin-top: -1px;

  top: 0;
  left: 0;
`
const UsageBar = styled.div`
  width: 556px;
  height: 8px;

  margin: 15px auto auto auto;

  // This is so the border doesn't affect the height
  box-sizing: content-box;

  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.orange500} 4.07%,
    #ffb800 101.05%
  );

  border: 2px solid ${props => props.theme.colors.coolGrey100};
  border-radius: 31px;
`
const HeaderContainer = styled.div`
  padding: 20px 0;

  display: flex;
  flex-direction: column;
  gap: 4px;

  border-bottom: 1px solid ${props => props.theme.colors.coolGrey300};
`
const Title = styled.h1`
  margin: 0;

  color: ${props => props.theme.colors.grey900};
  font-weight: 600;
  font-size: 24px;
  line-height: 24px;
`
const Description = styled.h1`
  color: ${props => props.theme.colors.grey600};
  font-weight: 400;
  font-size: 15px;
  line-height: 24px;
`
const UpgradeContainer = styled.div`
  display: flex;
  flex-direction: column;

  margin 0 8px;
  gap: 6px;
`
const UpgradeComparisonColumnIcon = styled(Icon)`
  color: ${props => props.theme.colors.orange500};
  height: 20px;
`
const UpgradeComparisonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`
const UpgradeComparisonColumnGroup = styled.div`
  display: flex;
  flex-direction: row;

  gap: 18px;
`
const UpgradeComparisonColumn = styled.div`
  color: ${props => props.theme.colors.grey900};
  display: flex;
  flex-direction: column;

  gap: 8px;

  font-weight: 400;
  font-size: 12px;
  line-height: 20px;

  &.center {
    text-align: center;
  }

  &.old {
    color: ${props => props.theme.colors.grey500};
  }

  &.new {
    color: ${props => props.theme.colors.grey900};
  }

  &.new > div:first-child {
    color: ${props => props.theme.colors.orange500};
  }

  & > div:first-child {
    font-size: 12px;
    line-height: 20px;

    text-transform: uppercase;
    font-weight: 500;
  }

  &.primary > div:first-child {
    font-size: 15px;
    text-transform: none;
    font-weight: 600;
  }
`
const UpgradePlanContainer = styled.div`
  background: ${props => props.theme.colors.orange100};
  border-radius: 4px;
  padding: 20px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 15px;
`
const UpgradePriceContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
`
const UpgradePrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  color: ${props => props.theme.colors.grey600};

  & > div:first-child {
    text-transform: uppercase;

    font-weight: 500;
    font-size: 12px;
    line-height: 14px;
  }

  & > .price {
    font-weight: 600;
    font-size: 24px;
    line-height: 28px;
  }

  & > .price > .interval {
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
  }

  &.new {
    color: ${props => props.theme.colors.grey900};
  }
`
const LearnMore = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.grey600};

  font-weight: 400;
  font-size: 12px;
  line-height: 20px;

  a {
    color: ${props => props.theme.colors.orange500};
    text-decoration: underline;
  }
`
const UpgradePriceIcon = styled(Icon)`
  color: ${props => props.theme.colors.orange500};
`
const NoThanks = styled.div`
  padding: 5px 10px;
  margin: auto;

  font-weight: 500;
  font-size: 11px;
  line-height: 16px;

  color: ${props => props.theme.colors.grey900};
  cursor: pointer;
`

const formatLimit = (t, feature) => {
  if (!feature) return ''

  const { featureLimit, intervalTimeframe } = feature
  switch (intervalTimeframe) {
    case 'month':
      return t(
        'upgradeModal.yourPlan.limit.monthly',
        `{{featureLimit}}/Month`,
        {
          featureLimit
        }
      )
    case 'day':
      return t('upgradeModal.yourPlan.limit.daily', `{{featureLimit}}/Day`, {
        featureLimit
      })
    default:
      return `${featureLimit}`
  }
}

const getUpgradeLink = (fromCheckout, checkoutLocale, billingFrequency) => {
  if (
    CHINESE_LANGUAGE_CHECKOUT_LOCALES.includes(checkoutLocale?.toUpperCase())
  ) {
    if (fromCheckout === 'js_checkout') {
      return CN_PRICING_PAGE_URL
    }

    return `${window.location.origin}/#${SETTINGS_URLS.subscriptions}`
  }

  return billingFrequency === 'monthly'
    ? 'https://members.junglescout.com/#/registrations?mt=4403b7423c7475d7ab3960dff9204bdf&lang=en-Us'
    : 'https://members.junglescout.com/#/registrations?mt=ba1a9b96c93ab092a9a84da5bb484035&lang=en-Us'
}

const getToolName = initiator => {
  switch (initiator) {
    case 'Product Database':
      return ['Product Database', 'productDatabase']
    case 'Opportunity Finder':
      return ['Opportunity Finder', 'opportunityFinder']
    case 'Product Tracker':
      return ['Product Tracker', 'productTracker']
    case 'Sales Estimator':
      return ['Sales Estimator', 'salesEstimator']
    case 'Listing Grader':
      return ['Listing Grader', 'listingGrader']
    case 'Keyword Scout':
      return ['Keyword Scout', 'keywordScout']
    case 'Supplier Database':
      return ['Supplier Database', 'supplierDatabase']
    case 'Supplier Tracker':
      return ['Supplier Tracker', 'supplierTracker']
    case 'Rank Tracker':
      return ['Rank Tracker', 'rankTracker']
    case 'Listing Builder':
      return ['Listing Builder', 'listingBuilder']
    default:
      return []
  }
}

/**
 * @name UpgradeSearchLimitModal
 * @description This component is for an experiment.
 *
 * @deprecated
 */
export const UpgradeSearchLimitModal = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    modal,
    featureLimits,
    membershipInfo: {
      from_checkout: fromCheckout,
      billing_frequency: billingFrequency,
      group
    },
    user: {
      currency,
      checkout_locale: checkoutLocale,
      selected_language: selectedLanguage
    }
  } = useSelector(state => state.globalData)

  const modalMetadata = useSelector(state => state.globalData.modalMetadata)
  const [toolName, featureLimitName] = getToolName(modalMetadata?.initiator)

  const getLimits = () => {
    if (modalMetadata) {
      switch (modalMetadata.initiator) {
        case 'Product Tracker':
          return featureLimits.productTracker
        case 'Supplier Tracker':
          return featureLimits.supplierTracker
        default:
          return featureLimits[featureLimitName]
      }
    }
    return featureLimits[featureLimitName]
  }

  const limit = getLimits()

  const pricingUrl = getPricingPageUrl(
    checkoutLocale,
    currency,
    selectedLanguage,
    group
  )

  const defaultDescription = () => {
    return (
      <Description>
        {t(
          'upgradeModal.description.text',
          'Your next {{featureLimit}} {{toolName}} searches will be available at {{time}}. To unlock unlimited searches, upgrade to a Suite Plan now.',
          {
            toolName,
            featureLimit: limit.featureLimit,
            time:
              limit.intervalTimeframe === 'month'
                ? t('upgradeModal.description.monthly', 'the end of the month')
                : t('upgradeModal.description.daily', '12 a.m. UTC')
          }
        )}
      </Description>
    )
  }

  const genericTrackerDescription = () => {
    return (
      <Description>
        {t(
          'upgradeModal.description.trackerText',
          'You have used all your available {{trackerName}} tracks. To unlock more {{trackerName}} tracks, upgrade to a Suite Plan now.',
          {
            trackerName: modalMetadata.initiator
          }
        )}
      </Description>
    )
  }

  const nonDynamicDescriptionsObject = {
    'Rank Tracker': t(
      'upgradeModal.description.rankTrackerText',
      'You have used all your available Rank Tracker keywords. To unlock more Rank Tracker keywords, upgrade to a Suite Plan now.'
    ),
    'Listing Builder': t(
      'upgradeModal.description.listingBuilderText',
      'You have used all your available listings. To unlock more listings, upgrade to a Suite Plan now.'
    )
  }

  const nonDynamicDescriptions = () => {
    return (
      <Description>
        {nonDynamicDescriptionsObject[modalMetadata.initiator]}
      </Description>
    )
  }

  const renderDescription = () => {
    if (modalMetadata) {
      switch (modalMetadata.initiator) {
        case 'Product Tracker':
        case 'Supplier Tracker':
          return genericTrackerDescription()
        case 'Rank Tracker':
        case 'Listing Builder':
          return nonDynamicDescriptions()
        default:
          return defaultDescription()
      }
    }
    return defaultDescription()
  }

  const handleUpgrade = () => {
    const destination = getUpgradeLink(
      fromCheckout,
      checkoutLocale,
      billingFrequency
    )
    sendCtaClickEvent({
      destination,
      text: 'Upgrade Now',
      location: 'Upsell Modal',
      extraProps: { testVersion: 'SMB-TEST-4' }
    })

    window.open(destination, '_blank')
  }

  const isOpen = modal === UPGRADE_LIMIT_MODAL

  if (isOpen && !(toolName || limit))
    throw new Error('Unsupported tool for upgrade limit modal')

  return (
    <Modal
      open={isOpen}
      onOpenChange={() => {
        dispatch(setGlobalModal())

        sendCtaClickEvent({
          destination: window.location.href,
          text: 'Modal Closed',
          location: 'Upsell Modal',
          extraProps: { testVersion: 'SMB-TEST-4' }
        })
      }}>
      <Modal.Content width="608px" maxWidth="608px">
        <ContentContainer>
          <Modal.Close>
            <CloseButton name="X_CLOSE" />
          </Modal.Close>
          <HeaderContainer>
            {limit && (
              <>
                <UsageBar />
                <UsageCounter>
                  {Math.min(limit.featureLimit, limit.featureUsageCount)}/
                  {limit.featureLimit}
                  <UsageCounterGraphic src={HoverGraphic} />
                </UsageCounter>
              </>
            )}
            <Title>{t('upgradeModal.title', 'You’re ready for more.')}</Title>
            {limit && renderDescription()}
          </HeaderContainer>
          <UpgradeContainer>
            <UpgradeComparisonContainer>
              <UpgradeComparisonColumn className="primary">
                <div>
                  {t(
                    'upgradeModal.comparison.basic',
                    'Everything in Basic plus:'
                  )}
                </div>
                <div>
                  {t(
                    'upgradeModal.comparison.productTracker',
                    'Product Tracker Limit:'
                  )}
                </div>
                <div>
                  {t(
                    'upgradeModal.comparison.productDatabase',
                    'Product Database searches:'
                  )}
                </div>
                <div>
                  {t(
                    'upgradeModal.comparison.keywordScout',
                    'Keyword Scout searches:'
                  )}
                </div>
                <div>
                  {t(
                    'upgradeModal.comparison.opportunityFinder',
                    'Opportunity Finder searches:'
                  )}
                </div>
                <div>
                  {t(
                    'upgradeModal.comparison.historicalProductTrackerData',
                    'Historical Product Tracker Data:'
                  )}
                </div>
                <div>
                  {t(
                    'upgradeModal.comparison.historicalKeywordData',
                    'Historical Keyword Data:'
                  )}
                </div>
                <div>
                  {t(
                    'upgradeModal.comparison.listingBuilder',
                    'Listing Builder:'
                  )}
                </div>
              </UpgradeComparisonColumn>
              <UpgradeComparisonColumnGroup>
                <UpgradeComparisonColumn className="center old">
                  <div>{t('upgradeModal.yourPlan.title', 'Your Plan')}</div>
                  <div>{formatLimit(t, featureLimits.productTracker)}</div>
                  <div>{formatLimit(t, featureLimits.productDatabase)}</div>
                  <div>{formatLimit(t, featureLimits.keywordScout)}</div>
                  <div>{formatLimit(t, featureLimits.opportunityFinder)}</div>
                  <div>
                    {t('upgradeModal.yourPlan.limit.1month', '1 Month')}
                  </div>
                  <div>
                    {t('upgradeModal.yourPlan.limit.1month', '1 Month')}
                  </div>
                  <div>
                    {t('upgradeModal.yourPlan.limit.noAccess', 'No Access')}
                  </div>
                </UpgradeComparisonColumn>
                <UpgradeComparisonColumn className="center">
                  <div>&nbsp;</div>
                  <UpgradeComparisonColumnIcon name="ARROW_RIGHT" width={10} />
                  <UpgradeComparisonColumnIcon name="ARROW_RIGHT" width={10} />
                  <UpgradeComparisonColumnIcon name="ARROW_RIGHT" width={10} />
                  <UpgradeComparisonColumnIcon name="ARROW_RIGHT" width={10} />
                  <UpgradeComparisonColumnIcon name="ARROW_RIGHT" width={10} />
                  <UpgradeComparisonColumnIcon name="ARROW_RIGHT" width={10} />
                  <UpgradeComparisonColumnIcon name="ARROW_RIGHT" width={10} />
                </UpgradeComparisonColumn>
                <UpgradeComparisonColumn className="center new">
                  <div>{t('upgradeModal.suite.title', 'Suite Plan')}</div>
                  <div>{t('upgradeModal.suite.limit.150', '150')}</div>
                  <div>
                    {t('upgradeModal.suite.limit.unlimited', 'Unlimited')}
                  </div>
                  <div>
                    {t('upgradeModal.suite.limit.unlimited', 'Unlimited')}
                  </div>
                  <div>
                    {t('upgradeModal.suite.limit.unlimited', 'Unlimited')}
                  </div>
                  <div>{t('upgradeModal.suite.limit.3months', '3 Months')}</div>
                  <div>{t('upgradeModal.suite.limit.1year', '1 Year')}</div>
                  <div>
                    {t('upgradeModal.suite.limit.fullAccess', 'Full Access')}
                  </div>
                </UpgradeComparisonColumn>
              </UpgradeComparisonColumnGroup>
            </UpgradeComparisonContainer>
          </UpgradeContainer>
          <LearnMore>
            <Trans i18nKey="upgradeModal.learnMore">
              Learn more about all our plans by viewing our{' '}
              <a href={pricingUrl} target="_blank" rel="noopener noreferrer">
                Pricing Page
              </a>
            </Trans>
          </LearnMore>
          <UpgradePlanContainer>
            <UpgradePriceContainer>
              <UpgradePrice>
                <div>{t('upgradeModal.yourPlan.title', 'Your Plan')}</div>
                <div className="price">
                  {billingFrequency === 'monthly' ? '$49' : '$349'}
                  <span className="interval">
                    {billingFrequency === 'monthly' ? '/mo' : '/yr'}
                  </span>
                </div>
              </UpgradePrice>
              <UpgradePriceIcon name="ARROW_RIGHT" width={18} />
              <UpgradePrice className="new">
                <div>{t('upgradeModal.suite.title', 'Suite Plan')}</div>
                <div className="price">
                  {billingFrequency === 'monthly' ? '$69' : '$589'}
                  <span className="interval">
                    {billingFrequency === 'monthly' ? '/mo' : '/yr'}
                  </span>
                </div>
              </UpgradePrice>
            </UpgradePriceContainer>
            <Button onClick={handleUpgrade}>
              {t('upgradeModal.upgrade', 'Upgrade Now')}
            </Button>
          </UpgradePlanContainer>
          <Modal.Close>
            <NoThanks role="button">
              {t('upgradeModal.noThanks', 'No Thanks')}
            </NoThanks>
          </Modal.Close>
        </ContentContainer>
      </Modal.Content>
    </Modal>
  )
}
